/**
 * OrbitPrint — auth context (own realm, own token key).
 *
 * `orbitApi` talks to /api/orbit/* and carries the `orbit_token` — a
 * separate localStorage key so an OrbitPrint session can never collide with
 * a POS (`agripos_token`) or Sports (`sports_token`) session on the same
 * browser. The backend refuses foreign-realm tokens anyway; this keeps the
 * client honest too.
 *
 * `base` is "" on orbitprint.agri-books.com (the app owns the root) and "/orbit"
 * everywhere else (a dev box, or the main domain). Pages build absolute paths
 * with `useOrbitPath()` so relative-route surprises never happen.
 *
 * Support session (super-admin "Open as owner", slice 3b — same design as Sports):
 * a 2-hour owner token arrives as `#orbit_support_token=<jwt>` on the app URL, is moved into
 * sessionStorage (one tab, dies with the tab, never clobbers a real login in localStorage),
 * stripped from the URL so it never lands in history, and takes precedence over a stored
 * login for this tab only. The backend stamps `imp_by` on it and refuses credential actions.
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const ORBIT_TOKEN_KEY = 'orbit_token';
export const ORBIT_SUPPORT_KEY = 'orbit_support_token';

// ── Support session ──────────────────────────────────────────────────────────
// The hash key is product-specific on purpose: the Sports auth module consumes a bare
// `#support_token=` at load and evaluates before this module, so a shared name would be
// swallowed into the Sports session store and never reach OrbitPrint.
export const ORBIT_SUPPORT_HASH = 'orbit_support_token';
const HASH_RE = /[#&]orbit_support_token=([^&]+)/;

const stripSupportHash = () => {
  if (HASH_RE.test(window.location.hash || '')) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
};

(() => {
  if (typeof window === 'undefined') return;
  const m = HASH_RE.exec(window.location.hash || '');
  if (m) {
    try { sessionStorage.setItem(ORBIT_SUPPORT_KEY, decodeURIComponent(m[1])); } catch { /* storage blocked: the hash simply stays */ }
    // A replaceState fired before `load` can be reverted when the browser commits the navigation
    // entry — strip now AND after load (the Sports lesson).
    stripSupportHash();
    if (document.readyState === 'complete') setTimeout(stripSupportHash, 0);
    else window.addEventListener('load', () => setTimeout(stripSupportHash, 0), { once: true });
  }
})();

export function getSupportSession() {
  let token = null;
  try { token = sessionStorage.getItem(ORBIT_SUPPORT_KEY); } catch { return null; }
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp * 1000 < Date.now()) { sessionStorage.removeItem(ORBIT_SUPPORT_KEY); return null; }
    return { token, expiresAt: new Date(payload.exp * 1000), by: payload.imp_by || '' };
  } catch { sessionStorage.removeItem(ORBIT_SUPPORT_KEY); return null; }
}

/** The token this tab should use: a support token wins over a stored login. */
export const getActiveOrbitToken = () => getSupportSession()?.token || localStorage.getItem(ORBIT_TOKEN_KEY);

export const orbitApi = axios.create({ baseURL: `${BACKEND_URL}/api/orbit` });
orbitApi.interceptors.request.use((config) => {
  const token = getActiveOrbitToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const OrbitAuthContext = createContext(null);

export function errText(err, fallback = 'Something went wrong. Try again.') {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return detail?.message || detail?.msg || fallback;
}

export function OrbitAuthProvider({ base = '/orbit', children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supportSession, setSupportSession] = useState(() => getSupportSession());

  const applySession = useCallback((data) => {
    if (data?.token) localStorage.setItem(ORBIT_TOKEN_KEY, data.token);
    setUser(data.user || null);
    setOrg(data.org || null);
    setOrgs(data.orgs || []);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(ORBIT_TOKEN_KEY);
    setUser(null); setOrg(null); setOrgs([]);
  }, []);

  /** Ends the support session for this tab only; a real login underneath is untouched. */
  const endSupportSession = useCallback(() => {
    try { sessionStorage.removeItem(ORBIT_SUPPORT_KEY); } catch { /* nothing to remove */ }
    setSupportSession(null);
    window.location.href = `${base}/login`;
  }, [base]);

  useEffect(() => { setTimeout(stripSupportHash, 0); }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!getActiveOrbitToken()) { setLoading(false); return; }
      try {
        const res = await orbitApi.get('/me');
        if (alive) {
          // /me never carries a token; a support session never writes to localStorage
          setUser(res.data.user || null); setOrg(res.data.org || null); setOrgs(res.data.orgs || []);
          if (res.data.support_session_by) setSupportSession((s) => s || getSupportSession());
        }
      } catch (err) {
        if (alive && err?.response?.status === 401) {
          // A dead support token must not evict a real owner login stored beneath it.
          if (getSupportSession()) { sessionStorage.removeItem(ORBIT_SUPPORT_KEY); setSupportSession(null); }
          else clearSession();
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [applySession, clearSession]);

  const login = useCallback(async (email, password) => {
    const res = await orbitApi.post('/auth/login', { email, password });
    applySession(res.data);
    return res.data;
  }, [applySession]);

  const register = useCallback(async (email, password, fullName) => {
    const res = await orbitApi.post('/auth/register', { email, password, full_name: fullName });
    applySession(res.data);
    return res.data;
  }, [applySession]);

  const switchOrg = useCallback(async (orgId) => {
    const res = await orbitApi.post('/auth/switch-org', { org_id: orgId });
    applySession(res.data);
    return res.data;
  }, [applySession]);

  const logout = useCallback(async () => {
    if (getSupportSession()) { endSupportSession(); return; }
    try { await orbitApi.post('/auth/logout'); } catch { /* stateless — dropping the token is what matters */ }
    clearSession();
  }, [clearSession, endSupportSession]);

  const value = useMemo(() => ({
    base, user, org, orgs, loading, login, register, logout, switchOrg, applySession, supportSession, endSupportSession,
  }), [base, user, org, orgs, loading, login, register, logout, switchOrg, applySession, supportSession, endSupportSession]);

  return <OrbitAuthContext.Provider value={value}>{children}</OrbitAuthContext.Provider>;
}

export function useOrbitAuth() {
  const ctx = useContext(OrbitAuthContext);
  if (!ctx) throw new Error('useOrbitAuth must be used inside OrbitAuthProvider');
  return ctx;
}

/** Absolute path inside the OrbitPrint app: p('/login') → '/login' or '/orbit/login'. */
export function useOrbitPath() {
  const { base } = useOrbitAuth();
  return useCallback((path) => `${base}${path.startsWith('/') ? path : `/${path}`}`, [base]);
}
